import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  BarChart3, 
  Download, 
  FileText, 
  Calendar,
  Users,
  Clock,
  Filter,
  Search,
  RefreshCw,
  Eye,
  Printer
} from 'lucide-react';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useEmployee } from '../../contexts/EmployeeContext';
import { useManager } from '../../contexts/ManagerContext';
import MusterRollReport from './MusterRollReport';
import toast from 'react-hot-toast';
import {
  REPORT_LOCATION_OPTIONS,
  attendanceAddressMatchesLocationKey
} from '../../constants/reportLocations';
import { toLocalDateKey, parseLocalDate } from '../../utils/localDateKey';

const ReportsSection = () => {
  const [searchParams] = useSearchParams();
  const {
    attendanceList,
    managers,
    employees,
    fetchAttendance,
    fetchManagers,
    fetchEmployees,
    exportToCSV,
    exportToPDF,
    isLoading
  } = useAttendance();

  const [selectedReport, setSelectedReport] = useState('muster-roll');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [filters, setFilters] = useState({
    managerId: '',
    shift: '',
    category: '',
    locationKey: ''
  });
  const [showPreview, setShowPreview] = useState(false);
  const [reportData, setReportData] = useState([]);

  // Function to reset to current month
  const resetToCurrentMonth = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setDateRange({
      startDate: toLocalDateKey(firstDayOfMonth),
      endDate: toLocalDateKey(lastDayOfMonth)
    });
    
    // Generate report after a short delay
    setTimeout(() => {
      generateMusterRollReport();
    }, 100);
  };

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchAttendance(),
          fetchManagers(),
          fetchEmployees()
        ]);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    loadData();
  }, [fetchAttendance, fetchManagers, fetchEmployees]);

  // Default date range: query params from seed deep-link, else current month
  useEffect(() => {
    const year = Number(searchParams.get('year'));
    const month = Number(searchParams.get('month'));
    const shiftRaw = String(searchParams.get('shift') || '').trim().toLowerCase();
    const shiftMap = { '1': 'morning', '2': 'evening', '3': 'night', morning: 'morning', evening: 'evening', night: 'night' };
    const shift = shiftMap[shiftRaw] || '';

    if (Number.isInteger(year) && year >= 2000 && Number.isInteger(month) && month >= 1 && month <= 12) {
      const firstDayOfMonth = new Date(year, month - 1, 1);
      const lastDayOfMonth = new Date(year, month, 0);
      setDateRange({
        startDate: toLocalDateKey(firstDayOfMonth),
        endDate: toLocalDateKey(lastDayOfMonth)
      });
      if (shift) {
        setFilters((prev) => ({ ...prev, shift }));
      }
      return;
    }

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setDateRange({
      startDate: toLocalDateKey(firstDayOfMonth),
      endDate: toLocalDateKey(lastDayOfMonth)
    });
  }, [searchParams]);

  // Auto-generate report when data is loaded or filters change
  useEffect(() => {
    if (employees.length > 0 || attendanceList.length > 0) {
      // Only generate if date range is set and we have data
      if (dateRange.startDate && dateRange.endDate) {
        // Auto-generating report due to filter change
        setTimeout(() => {
          generateMusterRollReport();
        }, 100);
      }
    }
  }, [employees.length, attendanceList.length, dateRange.startDate, dateRange.endDate, filters.shift, filters.locationKey]);

  // Auto-generate report when shift filter changes (separate effect for better control)
  useEffect(() => {
    if (employees.length > 0 && dateRange.startDate && dateRange.endDate && reportData.length > 0) {
      // Shift filter changed, auto-regenerating report
      setTimeout(() => {
        generateMusterRollReport();
      }, 50);
    }
  }, [filters.shift, filters.locationKey]);

  // Generate muster roll report data
  const generateMusterRollReport = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      toast.error('Please select start and end dates');
      return;
    }
    
    // Fetch attendance data for the selected date range.
    // Pass shift to API so employee+day dedupe does not drop morning when evening exists same day.
    let attendanceDataForReport = [];
    try {
      const result = await fetchAttendance({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: 1,
        limit: 10000, // Get all records for the date range
        ...(filters.locationKey ? { locationKey: filters.locationKey } : {}),
        ...(filters.shift ? { shift: filters.shift } : {})
      });
      // Use the returned data directly instead of waiting for state update
      attendanceDataForReport = result?.attendance || attendanceList || [];
    } catch (err) {
      console.error('Error fetching attendance for report:', err);
      toast.error('Failed to fetch attendance data');
      // Fallback to current attendanceList if fetch fails
      attendanceDataForReport = attendanceList || [];
    }

    const startDate = parseLocalDate(dateRange.startDate);
    const endDate = parseLocalDate(dateRange.endDate);
    const daysInMonth = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    // SHIFT column + shift filter: prefer shift on attendance rows in this date range.
    // Employee master `shift` is often stale (e.g. still "morning" after night-only imports),
    // which wrongly hides rows when filtering by Night / Evening / Morning.
    const shiftForMusterRow = (attendance, employee) => {
      if (!attendance?.length) return employee?.shift || 'N/A';
      const sorted = [...attendance].sort(
        (a, b) => new Date(a.stepIn) - new Date(b.stepIn)
      );
      const shifts = sorted.map((r) => r.shift).filter(Boolean);
      if (!shifts.length) return employee?.shift || 'N/A';
      const unique = [...new Set(shifts)];
      if (unique.length === 1) return unique[0];
      return shifts[0] || employee?.shift || 'N/A';
    };

    // Filter by date (and by record.shift when a shift is selected) so P marks match Attendance.
    let filteredAttendance = attendanceDataForReport.filter(record => {
      if (!record || !record.stepIn) return false;

      const recordDate = new Date(record.stepIn);
      recordDate.setHours(0, 0, 0, 0);
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      if (!(recordDate >= start && recordDate <= end)) return false;

      if (filters.shift) {
        const recordShift = String(record.shift || '').toLowerCase().trim();
        if (recordShift !== filters.shift.toLowerCase().trim()) return false;
      }
      return true;
    });

    if (filters.locationKey) {
      filteredAttendance = filteredAttendance.filter((record) =>
        attendanceAddressMatchesLocationKey(record.address, filters.locationKey)
      );
    }

    // Debug logging removed for production
    // Uncomment below for debugging if needed:
    // console.log('📊 Report Generation Debug:', {
    //   dateRange: { start: dateRange.startDate, end: dateRange.endDate },
    //   shiftFilter: filters.shift,
    //   totalAttendanceRecords: attendanceDataForReport.length,
    //   filteredAttendanceRecords: filteredAttendance.length
    // });

    // Group attendance by employee (using string IDs for consistent matching)
    const employeeAttendance = {};
    filteredAttendance.forEach(record => {
      const employeeId = record.employeeId?._id || record.employeeId;
      if (!employeeId) {
        console.warn('⚠️ Attendance record missing employeeId:', record);
        return;
      }
      // Use string ID for consistent key matching
      const employeeIdStr = employeeId.toString();
      if (!employeeAttendance[employeeIdStr]) {
        employeeAttendance[employeeIdStr] = [];
      }
      employeeAttendance[employeeIdStr].push(record);
    });

    // Generate report data
    let report = [];
    
    // Get unique employee IDs from attendance records (this ensures we count employees who actually have attendance)
    const employeeIdsWithAttendance = new Set(Object.keys(employeeAttendance));
    
    // Create a map of employees by ID for quick lookup
    const employeesMap = new Map();
    if (employees.length > 0) {
      employees.forEach(emp => {
        const empId = emp._id?.toString();
        if (empId) {
          employeesMap.set(empId, emp);
        }
      });
    }
    
    // Build report for ALL employees who have attendance records (not just those in employees list)
    // This ensures consistency with attendance report which shows all attendance records
    const reportEmployees = [];
    
    employeeIdsWithAttendance.forEach(employeeIdStr => {
      const employee = employeesMap.get(employeeIdStr);
      const attendance = employeeAttendance[employeeIdStr] || [];
      
      // If employee exists in master list, use that data
      // Otherwise, create a virtual employee from attendance record data
      if (employee) {
        // Use empCode directly - it should contain the original numeric code assigned at creation
        const empCode = employee.empCode || employee.employeeCode || '';
        reportEmployees.push({
          _id: employee._id,
          employeeCode: empCode,
          name: employee.name,
          designation: employee.designation || 'SECURITY GUARD',
          shift: shiftForMusterRow(attendance, employee),
          uan: employee.uan || 'N/A',
          esic: employee.esic || 'N/A',
          attendance: attendance
        });
      } else {
        // Employee not in master list - create from attendance record
        const firstRecord = attendance[0];
        const employeeId = firstRecord.employeeId?._id || firstRecord.employeeId;
        // Use empCode from attendance record if available
        const empCode = firstRecord.employeeId?.empCode || firstRecord.employeeId?.employeeCode || '';
        reportEmployees.push({
          _id: employeeId,
          employeeCode: empCode,
          name: firstRecord.employeeId?.name || 'Unknown Employee',
          designation: firstRecord.employeeId?.designation || 'SECURITY GUARD',
          shift: shiftForMusterRow(attendance, null) || firstRecord.shift || 'N/A',
          uan: firstRecord.employeeId?.uan || 'N/A',
          esic: firstRecord.employeeId?.esic || 'N/A',
          attendance: attendance
        });
      }
    });
    
    // Sort by employee name for consistent ordering
    reportEmployees.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    
    // Generate report rows
    report = reportEmployees.map((employee, index) => {
        const attendance = employee.attendance || [];
        const dailyAttendance = {};

        // Initialize daily attendance for the month
        for (let i = 1; i <= daysInMonth; i++) {
          const currentDate = new Date(startDate);
          currentDate.setDate(startDate.getDate() + i - 1);
          const dateKey = toLocalDateKey(currentDate);
          dailyAttendance[dateKey] = '';
        }

        // Debug logging removed for production

        // Fill in attendance data
        attendance.forEach((record, recordIndex) => {
          if (!record || !record.stepIn) {
            console.warn('⚠️ Invalid attendance record:', record);
            return;
          }
          const recordDate = toLocalDateKey(new Date(record.stepIn));
          if (dailyAttendance.hasOwnProperty(recordDate)) {
            // Use the status field if available, otherwise default to present if record exists
            // If an attendance record exists (has stepIn), employee is present by default
            if (record.status) {
              switch (record.status.toLowerCase()) {
                case 'present':
                  dailyAttendance[recordDate] = 'P';
                  break;
                case 'absent':
                  dailyAttendance[recordDate] = 'A';
                  break;
                case 'weekoff':
                  dailyAttendance[recordDate] = 'W';
                  break;
                default:
                  // If status is set but not recognized, default to present since record exists
                  dailyAttendance[recordDate] = 'P';
              }
            } else {
              // If status is not set but record exists (has stepIn), employee is present
              // This handles cases where employee has stepped in but status field is missing
              dailyAttendance[recordDate] = 'P';
            }
          }
          // Date mismatch handling - silently skip if date doesn't match range
        });

        // Calculate total days present
        const totalDays = Object.values(dailyAttendance).filter(day => day === 'P').length;

        return {
          srNo: index + 1,
          empCode: employee.employeeCode || employee.empCode || '',
          name: employee.name,
          designation: employee.designation,
          shift: employee.shift,
          uan: employee.uan,
          esic: employee.esic,
          dailyAttendance,
          totalDays
        };
      });

    // Shift dropdown: same source as SHIFT column (attendance-based shift for the period)
    if (filters.shift) {
      const filterShift = filters.shift.toLowerCase().trim();
      report = report.filter((row) => {
        const rowShift = String(row.shift || '').toLowerCase().trim();
        return rowShift === filterShift;
      });
      report = report.map((row, index) => ({ ...row, srNo: index + 1 }));
    }

    // If no attendance records found, use test data (fallback)
    if (report.length === 0 && employeeIdsWithAttendance.size === 0) {
      // Generate test data if no real employees - matching the reference format
      const testEmployees = [
        {
          name: 'MUKESH R LEUVA',
          designation: 'SECURITY OFFICER',
          shift: 'morning',
          uan: '101569928075',
          esic: '3713680043'
        },
        {
          name: 'HARSHAD KUMAR',
          designation: 'SECURITY OFFICER', 
          shift: 'evening',
          uan: '101569928076',
          esic: '3713680044'
        },
        {
          name: 'USHA M PATNI',
          designation: 'LADIES GUARD',
          shift: 'morning',
          uan: '101569928077',
          esic: '3713680045'
        },
        {
          name: 'PARMAR ASHOKKUMAR',
          designation: 'SECURITY GUARD',
          shift: 'night',
          uan: '101307875831',
          esic: '3713501469'
        },
        {
          name: 'JAGDISH R VANKAR',
          designation: 'SECURITY GUARD',
          shift: 'evening',
          uan: '101683177366',
          esic: '3713750393'
        }
      ];

      // Filter test employees by shift if specified
      let filteredTestEmployees = testEmployees;
      if (filters.shift) {
        // Filter test employees by shift
        filteredTestEmployees = testEmployees.filter(employee => {
          const employeeShift = employee.shift?.toLowerCase()?.trim();
          const filterShift = filters.shift?.toLowerCase()?.trim();
          return employeeShift === filterShift;
        });
        // Filtered test employees
        
        // No test employees match filter - handled by empty report warning below
      }

      report = filteredTestEmployees.map((employee, index) => {
        const dailyAttendance = {};

        // Initialize daily attendance for the month
        for (let i = 1; i <= daysInMonth; i++) {
          const currentDate = new Date(startDate);
          currentDate.setDate(startDate.getDate() + i - 1);
          const dateKey = toLocalDateKey(currentDate);
          
          // Generate realistic attendance pattern
          const dayOfWeek = currentDate.getDay();
          if (dayOfWeek === 0) { // Sunday
            dailyAttendance[dateKey] = 'W'; // Weekly off
          } else {
            // Random attendance: 90% present, 10% absent
            dailyAttendance[dateKey] = Math.random() < 0.90 ? 'P' : 'A';
          }
        }

        // Calculate total days present
        const totalDays = Object.values(dailyAttendance).filter(day => day === 'P').length;

        return {
          srNo: index + 1,
          empCode: String(13101 + index), // Use numeric code format like "13101"
          name: employee.name,
          designation: employee.designation,
          shift: employee.shift,
          uan: employee.uan,
          esic: employee.esic,
          dailyAttendance,
          totalDays
        };
      });
    }

    if (report.length === 0) {
      if (filters.shift) {
        toast(`No employees found for ${filters.shift} shift`, { icon: '⚠️' });
      } else {
        toast('No employees found', { icon: '⚠️' });
      }
    }
    
    // Report generation complete
    setReportData(report);
    setShowPreview(true);
    toast.success(`Report generated successfully with ${report.length} employees`);
  };

  // Export to Excel in traditional muster roll format
  const exportMusterRollExcel = async () => {
    if (reportData.length === 0) {
      toast.error('No report data to export');
      return;
    }

    try {
      // Import xlsx library
      const XLSX = await import('xlsx');
      
      const startDate = parseLocalDate(dateRange.startDate);
      const endDate = parseLocalDate(dateRange.endDate);
      const monthYear = startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
      const deploymentSite = filters.locationKey
        ? (
            REPORT_LOCATION_OPTIONS.find((o) => o.value === filters.locationKey)?.label ||
            'SELECTED SITE'
          ).toUpperCase()
        : 'ALL SITES';
      const deploymentHeadline = `DEPLOYMENT OF SECURITY PERSON AT ${deploymentSite} ON ${monthYear}`;
      const daysInMonth = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Prepare data array
      const excelData = [];

      // Add header rows
      excelData.push(['Form XVI 1 [See Rule 78(1) (a) (1)] Muster Roll']);
      excelData.push(['PANTHER SECURE']);
      excelData.push([deploymentHeadline]);
      excelData.push([]); // Empty row

      // Create headers array
      const headers = ['SR NO', 'EMP CODE', 'NAME OF EMPLOYEE', 'DESIGNATION', 'SHIFT', 'UAN', 'ESIC'];
      
      // Add day headers
      for (let i = 1; i <= daysInMonth; i++) {
        headers.push(i.toString());
      }
      headers.push('TOTAL DAYS');
      
      excelData.push(headers);

      // Add employee data rows
      reportData.forEach(record => {
        const row = [
          record.srNo,
          record.empCode,
          record.name,
          record.designation,
          record.shift,
          record.uan,
          record.esic
        ];
        
        // Add daily attendance
        for (let i = 1; i <= daysInMonth; i++) {
          const currentDate = new Date(startDate);
          currentDate.setDate(startDate.getDate() + i - 1);
          const dateKey = toLocalDateKey(currentDate);
          row.push(record.dailyAttendance[dateKey] || '');
        }
        
        row.push(record.totalDays);
        excelData.push(row);
      });

      // Add total row at the bottom
      const totalRow = ['TOTAL EMPLOYEES', '', '', '', '', '', ''];
      
      // Calculate total present (P) for each day
      for (let i = 1; i <= daysInMonth; i++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + i - 1);
        const dateKey = toLocalDateKey(currentDate);
        
        // Count how many employees were present (P) on this day
        const totalPresentForDay = reportData.reduce((sum, record) => {
          const dayStatus = record.dailyAttendance[dateKey];
          return sum + (dayStatus === 'P' ? 1 : 0);
        }, 0);
        
        totalRow.push(totalPresentForDay);
      }
      
      // Add total of all present days
      const totalAllDays = reportData.reduce((sum, record) => sum + record.totalDays, 0);
      totalRow.push(totalAllDays);
      
      excelData.push(totalRow);

      // Create worksheet from data
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths for better readability
      const colWidths = [
        { wch: 8 },   // SR NO
        { wch: 15 },  // EMP CODE
        { wch: 30 },  // NAME OF EMPLOYEE
        { wch: 20 },  // DESIGNATION
        { wch: 12 },  // SHIFT
        { wch: 15 },  // UAN
        { wch: 15 },  // ESIC
      ];
      
      // Add day column widths (narrow for dates)
      for (let i = 1; i <= daysInMonth; i++) {
        colWidths.push({ wch: 5 });
      }
      colWidths.push({ wch: 12 }); // TOTAL DAYS
      
      worksheet['!cols'] = colWidths;

      // Style the total row (last row) - make it bold
      const totalRowIndex = excelData.length - 1; // Last row index (0-based)
      
      // Style individual cells in total row
      for (let col = 0; col < headers.length; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
        if (!worksheet[cellAddress]) worksheet[cellAddress] = { t: 's', v: '' };
        if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
        worksheet[cellAddress].s.font = { bold: true };
        worksheet[cellAddress].s.fill = { fgColor: { rgb: "E0E0E0" } }; // Light gray background
        worksheet[cellAddress].s.alignment = { horizontal: "center", vertical: "center" };
      }

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Muster Roll');

      // Generate Excel file
      const filename = `muster_roll_${monthYear.replace(' ', '_')}.xlsx`;
      
      // Check if in WebView and use appropriate download method
      if (window.ReactNativeWebView && window.downloadExcel) {
        // Convert to base64 for WebView
        const excelBuffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
        // Convert Uint8Array to base64 properly
        let binary = '';
        const bytes = new Uint8Array(excelBuffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64Data = btoa(binary);
        const dataUri = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64Data}`;
        window.downloadExcel(dataUri, filename);
        toast.success('Muster roll Excel file export initiated in mobile app');
      } else {
        // Use manual blob download for better browser compatibility (Windows 7/8 support)
        try {
          // Try XLSX.writeFile first (modern browsers)
          if (typeof XLSX.writeFile === 'function') {
            XLSX.writeFile(workbook, filename);
            toast.success('Muster roll Excel file exported successfully');
          } else {
            // Fallback: Manual blob download for older browsers
            throw new Error('XLSX.writeFile not available');
          }
        } catch (error) {
          // Fallback method for older browsers (Windows 7/8, IE11, etc.)
          console.warn('XLSX.writeFile failed, using blob fallback:', error);
          
          try {
            // Generate Excel file as array buffer
            const excelBuffer = XLSX.write(workbook, { 
              type: 'array', 
              bookType: 'xlsx',
              cellStyles: true 
            });
            
            // Create blob with proper MIME type
            // Check for Blob constructor support (IE10+)
            let blob;
            if (typeof Blob !== 'undefined') {
              blob = new Blob([excelBuffer], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
              });
            } else {
              // Very old browser fallback - use BlobBuilder (deprecated but works in IE10)
              const BlobBuilder = window.BlobBuilder || window.WebKitBlobBuilder || window.MozBlobBuilder;
              if (BlobBuilder) {
                const bb = new BlobBuilder();
                bb.append(excelBuffer);
                blob = bb.getBlob('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
              } else {
                throw new Error('Blob API not supported');
              }
            }
            
            // Check for URL.createObjectURL support (older browsers)
            if (window.URL && window.URL.createObjectURL) {
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              
              // Set download attribute (may not work in IE11, but we'll try)
              if (link.download !== undefined) {
                link.setAttribute('download', filename);
              }
              
              link.href = url;
              link.style.display = 'none';
              document.body.appendChild(link);
              
              // Trigger click
              if (document.createEvent) {
                const event = document.createEvent('MouseEvents');
                event.initEvent('click', true, true);
                link.dispatchEvent(event);
              } else {
                link.click();
              }
              
              // Clean up
              setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              }, 100);
              
              toast.success('Muster roll Excel file exported successfully');
            } else {
              // Last resort: Use data URI (works in very old browsers but has size limits)
              const reader = new FileReader();
              reader.onload = function(e) {
                const dataUri = e.target.result;
                const link = document.createElement('a');
                link.href = dataUri;
                link.download = filename;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                  document.body.removeChild(link);
                }, 100);
                toast.success('Muster roll Excel file exported successfully');
              };
              reader.onerror = () => {
                toast.error('Failed to export Excel file. Please try a different browser.');
                console.error('FileReader error');
              };
              reader.readAsDataURL(blob);
            }
          } catch (fallbackError) {
            console.error('All download methods failed:', fallbackError);
            toast.error('Failed to export Excel file. Your browser may not support file downloads. Please try updating your browser or using Chrome/Firefox.');
          }
        }
      }
    } catch (err) {
      toast.error('Failed to export Excel file');
      console.error('Error exporting Excel:', err);
    }
  };

  // Export traditional format PDF
  const exportTraditionalPDF = async () => {
    if (reportData.length === 0) {
      toast.error('No report data to export');
      return;
    }

    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('landscape', 'mm', 'a3'); // Using A3 for better fit

      const startDate = parseLocalDate(dateRange.startDate);
      const endDate = parseLocalDate(dateRange.endDate);
      const monthYear = startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
      const deploymentSite = filters.locationKey
        ? (
            REPORT_LOCATION_OPTIONS.find((o) => o.value === filters.locationKey)?.label ||
            'SELECTED SITE'
          ).toUpperCase()
        : 'ALL SITES';
      const deploymentText = `DEPLOYMENT OF SECURITY PERSON AT ${deploymentSite} ON ${monthYear}`;
      const daysInMonth = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      // Page setup
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 10;

      let currentY = margin + 10;

      // Header section - matching traditional format with logo
      try {
        // Load and add the logo image
        const logoImg = new Image();
        logoImg.src = '/assets/logo.png';
        
        // Wait for image to load
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          // Set a timeout in case the image doesn't load
          setTimeout(reject, 5000);
        });
        
        // Add the logo image to PDF (smaller size for A3 format)
        doc.addImage(logoImg, 'PNG', margin, currentY - 5, 15, 15);
      } catch (error) {
        console.warn('Could not load logo image for muster roll PDF:', error);
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('Form XVI 1 [See Rule 78(1) (a) (1)] Muster Roll', pageWidth/2, currentY, { align: 'center' });
      currentY += 8;

      doc.setFontSize(14);
      doc.text('PANTHER SECURE', pageWidth/2, currentY, { align: 'center' });
      currentY += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(deploymentText, pageWidth/2, currentY, { align: 'center' });
      currentY += 15;

      // Table structure - traditional format
      const startX = margin;
      let currentX = startX;
      
      // Column widths (adjusted for A3)
      const colWidths = {
        srNo: 12,
        empCode: 18, 
        name: 50,
        designation: 25,
        shift: 20,
        uan: 25,
        esic: 25,
        daily: 6, // Each day column
        totalDays: 15
      };

      // Draw table headers
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setDrawColor(0);
      doc.setLineWidth(0.3);

      const tableStartY = currentY;
      const rowHeight = 15; // Increased for better readability

      // Header cells
      const headers = [
        'SR NO', 'EMP CODE', 'NAME OF EMPLOYEE', 'DESIGNATION', 
        'SHIFT', 'UAN', 'ESIC'
      ];

      // Add day headers
      for (let i = 1; i <= daysInMonth; i++) {
        headers.push(i.toString());
      }
      headers.push('TOTAL\nDAYS');

      // Draw header row
      currentX = startX;
      headers.forEach((header, index) => {
        let width;
        if (index < 6) {
          const keys = ['srNo', 'empCode', 'name', 'designation', 'shift', 'uan', 'esic'];
          width = colWidths[keys[index]];
        } else if (index < 6 + daysInMonth) {
          width = colWidths.daily;
        } else {
          width = colWidths.totalDays;
        }

        // Draw cell border
        doc.rect(currentX, currentY - 5, width, rowHeight);
        
        // Add text (handle multi-line headers)
        const lines = header.split('\n');
        const lineHeight = 3;
        const textStartY = currentY + (rowHeight/2) - ((lines.length * lineHeight)/2);
        
        lines.forEach((line, lineIndex) => {
          doc.text(line, currentX + width/2, textStartY + (lineIndex * lineHeight), { align: 'center' });
        });
        
        currentX += width;
      });

      currentY += rowHeight;

      // Data rows
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);

      reportData.forEach((record, rowIndex) => {
        // Check for new page
        if (currentY > pageHeight - 30) {
          doc.addPage();
          currentY = margin + 10;
        }

        currentX = startX;
        const dataRowHeight = 12;

        // Row data
        const rowData = [
          record.srNo.toString(),
          record.empCode,
          record.name,
          record.designation,
          record.shift,
          record.uan,
          record.esic
        ];

        // Add daily attendance
        for (let i = 1; i <= daysInMonth; i++) {
          const currentDate = new Date(startDate);
          currentDate.setDate(startDate.getDate() + i - 1);
          const dateKey = toLocalDateKey(currentDate);
          rowData.push(record.dailyAttendance[dateKey] || '');
        }

        rowData.push(record.totalDays.toString());

        // Draw data cells
        rowData.forEach((cellData, cellIndex) => {
          let width;
          if (cellIndex < 6) {
            const keys = ['srNo', 'empCode', 'name', 'designation', 'shift', 'uan', 'esic'];
            width = colWidths[keys[cellIndex]];
          } else if (cellIndex < 6 + daysInMonth) {
            width = colWidths.daily;
          } else {
            width = colWidths.totalDays;
          }

          // Draw cell border
          doc.rect(currentX, currentY - 4, width, dataRowHeight);
          
          // Handle long text (especially names)
          let displayText = cellData.toString();
          if (cellIndex === 2 && displayText.length > 30) { // Name column
            displayText = displayText.substring(0, 28) + '..';
          }
          
          // Color coding for attendance
          if (cellIndex >= 6 && cellIndex < 6 + daysInMonth) {
            if (displayText === 'P') doc.setTextColor(0, 100, 0); // Green for present
            else if (displayText === 'A') doc.setTextColor(200, 0, 0); // Red for absent
            else if (displayText === 'W') doc.setTextColor(0, 0, 200); // Blue for weekly off
            else doc.setTextColor(0, 0, 0); // Black for others
          } else {
            doc.setTextColor(0, 0, 0); // Black for other columns
          }
          
          // Center text in cell
          doc.text(displayText, currentX + width/2, currentY + dataRowHeight/2, { align: 'center' });
          
          currentX += width;
        });

        currentY += dataRowHeight;
      });

      // Save the PDF with WebView support
      const filename = `traditional_muster_roll_${monthYear.replace(' ', '_')}.pdf`;
      
      // Check if in WebView and use appropriate download method
      if (window.ReactNativeWebView && window.downloadPDF) {
        // Get PDF as base64 data URI
        const pdfDataUri = doc.output('datauristring');
        window.downloadPDF(pdfDataUri, filename);
        toast.success('Traditional muster roll PDF export initiated in mobile app');
      } else {
        // Fallback for regular browser
        doc.save(filename);
        toast.success('Traditional muster roll PDF exported successfully');
      }

    } catch (err) {
      console.error('Error exporting traditional PDF:', err);
      toast.error('Failed to export PDF: ' + err.message);
    }
  };

  const reportTypes = [
    {
      id: 'muster-roll',
      name: 'Muster Roll Report',
      description: 'Daily attendance muster roll in Form XVI format',
      icon: FileText,
      color: 'bg-blue-500'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Reports</h1>
          <p className="text-secondary-600">Generate and download various reports</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="btn btn-outline btn-sm flex items-center space-x-2"
          >
            <Eye className="h-4 w-4" />
            <span>{showPreview ? 'Hide' : 'Show'} Preview</span>
          </button>
        </div>
      </div>

      {/* Report Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {reportTypes.map((report) => (
          <div
            key={report.id}
            onClick={() => setSelectedReport(report.id)}
            className={`card cursor-pointer transition-all duration-200 ${
              selectedReport === report.id 
                ? 'ring-2 ring-primary-500 bg-primary-50' 
                : 'hover:shadow-md'
            }`}
          >
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-lg ${report.color}`}>
                <report.icon className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-secondary-900">{report.name}</h3>
                <p className="text-sm text-secondary-600">{report.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Report Parameters */}
      {selectedReport && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-secondary-900">Report Parameters</h3>
            <div className="text-sm text-secondary-600">
              Current Month: <span className="font-semibold text-primary-600">
                {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="label">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="input"
              />
              {dateRange.startDate && (
                <div className="text-xs text-secondary-500 mt-1">
                  Selected: {parseLocalDate(dateRange.startDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>
              )}
            </div>
            <div>
              <label className="label">End Date</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="input"
              />
              {dateRange.endDate && (
                <div className="text-xs text-secondary-500 mt-1">
                  Selected: {parseLocalDate(dateRange.endDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>
              )}
            </div>
            {/* <div>
              <label className="label">Manager</label>
              <select
                className="input"
                value={filters.managerId}
                onChange={(e) => setFilters({ ...filters, managerId: e.target.value })}
              >
                <option value="">All Managers</option>
                {managers.map(manager => (
                  <option key={manager._id} value={manager._id}>
                    {manager.name}
                  </option>
                ))}
              </select>
            </div> */}
            <div>
              <label className="label">Shift</label>
              <select
                className="input"
                value={filters.shift}
                onChange={(e) => setFilters({ ...filters, shift: e.target.value })}
              >
                <option value="">All Shifts</option>
                <option value="morning">Morning Shift</option>
                <option value="evening">Evening Shift</option>
                <option value="night">Night Shift</option>
              </select>
              {filters.shift && (
                <div className="text-xs text-secondary-500 mt-1">
                  Filtering by: {filters.shift.charAt(0).toUpperCase() + filters.shift.slice(1)} Shift
                  {/* <span className="text-green-600 ml-2">✓ Auto-updating</span> */}
                </div>
              )}
            </div>
            <div>
              <label className="label">Location</label>
              <select
                className="input"
                value={filters.locationKey}
                onChange={(e) => setFilters({ ...filters, locationKey: e.target.value })}
              >
                {REPORT_LOCATION_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {filters.locationKey && (
                <div className="text-xs text-secondary-500 mt-1">
                  Filtering by site address pattern for this location
                </div>
              )}
            </div>
            
            {/* Clear Shift Filter Button - positioned after shift dropdown */}
            {filters.shift && (
              <div className="flex items-start pt-6">
                <button
                  onClick={() => setFilters({ ...filters, shift: '' })}
                  className="btn btn-outline btn-sm"
                  title="Clear shift filter"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Clear Shift Filter
                </button>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center mt-4">
            <div className="flex space-x-3">
              {/* Clear Shift Filter button moved to be next to shift dropdown */}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={resetToCurrentMonth}
                className="btn btn-warning"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Reset to Current Month (
                {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})
              </button>
              <button
                onClick={generateMusterRollReport}
                className="btn btn-primary"
                disabled={!dateRange.startDate || !dateRange.endDate || isLoading}
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Generate Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Options - Updated with Traditional Format */}
      {showPreview && reportData.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Export Report</h3>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={exportMusterRollExcel}
              className="btn btn-success flex items-center"
            >
              <FileText className="h-4 w-4 mr-2" />
              Export as Excel (Traditional Format)
            </button>
            <button
              onClick={exportTraditionalPDF}
              className="btn btn-primary flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export as PDF (Traditional Format)
            </button>
            <button
              onClick={() => window.print()}
              className="btn btn-outline flex items-center"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Report
            </button>
          </div>
        </div>
      )}

      {/* Report Preview - Updated to match traditional format */}
      {showPreview && reportData.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">
            Traditional Muster Roll Report Preview
          </h3>
          
          {/* Header Information */}
          <div className="text-center mb-6 border-b pb-4">
            <h2 className="text-lg font-bold">Form XVI 1 [See Rule 78(1) (a) (1)] Muster Roll</h2>
                            <h3 className="text-base font-semibold mt-1">PANTHER SECURE</h3>
            <p className="text-sm mt-1">
              DEPLOYMENT OF SECURITY PERSON AT{' '}
              {filters.locationKey
                ? (
                    REPORT_LOCATION_OPTIONS.find((o) => o.value === filters.locationKey)?.label ||
                    'SELECTED SITE'
                  ).toUpperCase()
                : 'ALL SITES'}{' '}
              ON{' '}
              {dateRange.startDate
                ? parseLocalDate(dateRange.startDate).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  }).toUpperCase()
                : new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-400 text-xs">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-400 px-1 py-2 text-center font-bold">SR NO</th>
                  <th className="border border-gray-400 px-1 py-2 text-center font-bold">EMP CODE</th>
                  <th className="border border-gray-400 px-2 py-2 text-center font-bold">NAME OF EMPLOYEE</th>
                  <th className="border border-gray-400 px-1 py-2 text-center font-bold">DESIGNATION</th>
                  <th className="border border-gray-400 px-1 py-2 text-center font-bold">SHIFT</th>
                  <th className="border border-gray-400 px-1 py-2 text-center font-bold">UAN</th>
                  <th className="border border-gray-400 px-1 py-2 text-center font-bold">ESIC</th>
                  {dateRange.startDate && dateRange.endDate && (() => {
                    const startDate = parseLocalDate(dateRange.startDate);
                    const endDate = parseLocalDate(dateRange.endDate);
                    const daysInMonth = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                    const days = [];
                    for (let i = 1; i <= daysInMonth; i++) {
                      days.push(
                        <th key={i} className="border border-gray-400 px-1 py-2 text-center font-bold min-w-[25px]">
                          {i}
                        </th>
                      );
                    }
                    return days;
                  })()}
                  <th className="border border-gray-400 px-1 py-2 text-center font-bold">TOTAL<br/>DAYS</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((record) => (
                  <tr key={record.srNo} className="hover:bg-gray-50">
                    <td className="border border-gray-400 px-1 py-2 text-center font-medium">{record.srNo}</td>
                    <td className="border border-gray-400 px-1 py-2 text-center">{record.empCode}</td>
                    <td className="border border-gray-400 px-2 py-2 font-medium">{record.name}</td>
                    <td className="border border-gray-400 px-1 py-2 text-center">{record.designation}</td>
                    <td className="border border-gray-400 px-1 py-2 text-center capitalize">{record.shift || 'N/A'}</td>
                    <td className="border border-gray-400 px-1 py-2 text-center text-xs">{record.uan}</td>
                    <td className="border border-gray-400 px-1 py-2 text-center text-xs">{record.esic}</td>
                    {dateRange.startDate && dateRange.endDate && (() => {
                      const startDate = parseLocalDate(dateRange.startDate);
                      const endDate = parseLocalDate(dateRange.endDate);
                      const daysInMonth = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                      const days = [];
                      for (let i = 1; i <= daysInMonth; i++) {
                        const currentDate = new Date(startDate);
                        currentDate.setDate(startDate.getDate() + i - 1);
                        const dateKey = toLocalDateKey(currentDate);
                        const attendance = record.dailyAttendance[dateKey] || '';
                        days.push(
                          <td key={i} className={`border border-gray-400 px-1 py-2 text-center font-bold ${
                            attendance === 'P' ? 'text-green-600' : 
                            attendance === 'A' ? 'text-red-600' : 
                            attendance === 'W' ? 'text-blue-600' : 
                            'text-gray-400'
                          }`}>
                            {attendance}
                          </td>
                        );
                      }
                      return days;
                    })()}
                    <td className="border border-gray-400 px-1 py-2 text-center font-bold text-blue-600">{record.totalDays}</td>
                  </tr>
                                  ))}
                  
                  {/* Total Row */}
                  <tr className="bg-gray-100 font-bold">
                    <td className="border border-gray-400 px-1 py-2 text-center" colSpan="7">
                      TOTAL EMPLOYEES
                    </td>
                    {dateRange.startDate && dateRange.endDate && (() => {
                      const startDate = parseLocalDate(dateRange.startDate);
                      const endDate = parseLocalDate(dateRange.endDate);
                      const daysInMonth = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                      const days = [];
                      for (let i = 1; i <= daysInMonth; i++) {
                        const currentDate = new Date(startDate);
                        currentDate.setDate(startDate.getDate() + i - 1);
                        const dateKey = toLocalDateKey(currentDate);
                        
                        // Calculate total present (P) for this day across all employees
                        const totalPresentForDay = reportData.reduce((sum, record) => {
                          const dayStatus = record.dailyAttendance[dateKey];
                          return sum + (dayStatus === 'P' ? 1 : 0);
                        }, 0);
                        
                        days.push(
                          <td key={i} className="border border-gray-400 px-1 py-2 text-center font-bold text-green-600">
                            {totalPresentForDay}
                          </td>
                        );
                      }
                      return days;
                    })()}
                    <td className="border border-gray-400 px-1 py-2 text-center font-bold text-blue-600">
                      {reportData.reduce((sum, record) => sum + record.totalDays, 0)}
                    </td>
                  </tr>
                </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-sm mb-2">Legend:</h4>
            <div className="flex flex-wrap gap-4 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 bg-green-100 border border-green-300 rounded flex items-center justify-center text-green-600 font-bold">P</span>
                Present
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 bg-red-100 border border-red-300 rounded flex items-center justify-center text-red-600 font-bold">A</span>
                Absent
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 bg-blue-100 border border-blue-300 rounded flex items-center justify-center text-blue-600 font-bold">W</span>
                Weekly Off
              </span>
            </div>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {showPreview && reportData.length === 0 && (
        <div className="card">
          <div className="text-center py-8">
            <BarChart3 className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <p className="text-secondary-600">No report data available. Please generate a report first.</p>
          </div>
        </div>
      )}

    </div>
  );
};

export default ReportsSection;